// services/kyc/idVerificationService.ts
//
// KYC ID verification service

import { Capacitor } from '@capacitor/core';
import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { isNativeCapacitorApp } from '@/integrations/clients/carAuction/nativeAuth';

export type KycStatus = 'pending' | 'approved' | 'declined' | 'in_review' | null;
export type BankIdStatus = 'pending' | 'approved' | 'declined' | null;
export type VerificationMethod = 'bankid' | 'didit';

export interface KycStatusResponse {
  success: boolean;
  kycStatus: KycStatus;
  bankIdStatus: BankIdStatus;
  bankIdFailureReason: 'ssn_mismatch' | 'missing_registered_ssn' | 'ssn_already_used' | null;
  hasPersonalIdentityNumber?: boolean;
}

export interface StartKycResponse {
  success: boolean;
  sessionUrl: string;
}

export class IdVerificationService {
  /**
   * Start either BankID/Idura or Didit verification flow
   */
  static async start(method: VerificationMethod): Promise<StartKycResponse> {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api/v1';

    if (method === 'bankid') {
      if (isNativeCapacitorApp()) {
        const data = await api.post<{ success: boolean; continueUrl: string }>(
          '/users/bankid/verify/native-start',
          { nativePlatform: Capacitor.getPlatform() },
        );
        return { success: true, sessionUrl: data.continueUrl };
      }
      return {
        success: true,
        sessionUrl: `${apiBaseUrl}/users/bankid/verify`,
      };
    }

    return api.post<StartKycResponse>('/users/kyc/start');
  }

  /**
   * Get current KYC status
   */
  static async getStatus(): Promise<KycStatusResponse> {
    const data = await api.get<KycStatusResponse>('/users/kyc/status');
    
    // Normalize status to lowercase (backend may return uppercase enum values)
    if (data.kycStatus && typeof data.kycStatus === 'string') {
      data.kycStatus = data.kycStatus.toLowerCase() as KycStatus;
    }

    if (data.bankIdStatus && typeof data.bankIdStatus === 'string') {
      data.bankIdStatus = data.bankIdStatus.toLowerCase() as BankIdStatus;
    }

    return data;
  }
}

